<script setup lang="ts">
import { CopyDocument } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { computed } from "vue";
import { showError } from "~/utils/handleAPIError";
import { useFloatInput } from "../composables/useFloatInput";

const props = defineProps<{
  modelValue?: number;
  disabled?: boolean;
  min?: number;
  max?: number;
  precision?: number;
  enableCopy?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: number | undefined): void;
}>();

// Make sure that the price is reactive
// so that it changes when props.modelValue changes
const priceRef = computed(() => props.modelValue);
const { stringValue, handleBlur } = useFloatInput(priceRef, {
  min: props.min,
  max: props.max,
  digits: props.precision,
  onUpdate: (updated) => emit("update:modelValue", updated),
});

const stringValueWithCommas = computed(() =>
  stringValue.value.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
);
const handleRemoveCommas = (val: string): string => {
  return val.replaceAll(",", "");
};

const copyTextToClipboard = async () => {
  if (stringValue.value !== undefined) {
    try {
      await navigator.clipboard.writeText(stringValue.value.toString());
      ElMessage.success("Copied to clipboard");
    } catch {
      showError("Failed to copy to clipboard. Please try again.");
    }
  }
};
</script>

<template>
  <div class="price-input-container">
    <el-icon
      v-if="enableCopy && stringValue"
      class="copyBtn"
      title="Copy value to the clipboard"
      @click="copyTextToClipboard()"
    >
      <CopyDocument />
    </el-icon>
    <el-input
      :model-value="stringValueWithCommas"
      class="price-input-styling min-w-36"
      :disabled="disabled"
      :controls="false"
      align="right"
      type="currency"
      @update:model-value="
        (val: string) => (stringValue = handleRemoveCommas(val))
      "
      @blur="handleBlur"
    >
      <!-- Propogate child slots so we can use them in the parent  -->
      <template #prepend>
        <slot name="prepend"> $ </slot>
      </template>
      <template v-if="$slots.append" #append> <slot name="append" /></template>
    </el-input>
  </div>
</template>

<style less>
.price-input-container {
  .price-input-styling {
    .el-input-group__prepend {
      padding: 0 8px;
    }

    .el-input-group__append {
      padding: 0 8px;
    }

    .el-input__inner {
      text-align: right;
    }
  }
  .copyBtn {
    cursor: pointer;
    margin-right: 5px;
    vertical-align: middle;
  }
}
</style>
